import type { CreateReportRequest, ReportRow, ReportType, UpdateReportRequest } from '../../api/index.ts';
import { parseMajorAmount } from './money.ts';

export interface ReportFormState {
  shopId: number | '';
  date: string;
  gross: string;
  payout: string;
  tax: string;
  reportType: ReportType;
  currencyCode: string;
}

export function initialReportForm(
  report: ReportRow | null,
  defaultReportType: ReportType,
  defaultShopId?: number,
): ReportFormState {
  if (!report) {
    return {
      shopId: defaultShopId ?? '',
      date: '',
      gross: '',
      payout: '',
      tax: '',
      reportType: defaultReportType,
      currencyCode: '',
    };
  }
  return {
    shopId: report.shop_id,
    date: report.submission_date,
    gross: (report.sales_minor / 100).toFixed(2),
    payout: (report.payout_minor / 100).toFixed(2),
    tax: (report.tax_minor / 100).toFixed(2),
    reportType: report.report_type,
    currencyCode: report.currency_code ?? '',
  };
}

/** Returns the first validation problem, or null when the form is submittable. */
export function validateReportForm(form: ReportFormState, isEdit: boolean): string | null {
  if (!isEdit && form.shopId === '') return 'Choose a shop.';
  if (form.date.trim() === '') return 'Choose a date.';
  if (parseMajorAmount(form.gross) === null) return 'Gross sales must be a non-negative amount with at most 2dp.';
  if (parseMajorAmount(form.payout) === null) return 'Payout must be a non-negative amount with at most 2dp.';
  if (parseMajorAmount(form.tax) === null) return 'Tax must be a non-negative amount with at most 2dp.';
  return null;
}

function moneyFields(form: ReportFormState) {
  return {
    submission_date: form.date,
    sales_minor: parseMajorAmount(form.gross) ?? 0,
    payout_minor: parseMajorAmount(form.payout) ?? 0,
    tax_minor: parseMajorAmount(form.tax) ?? 0,
    report_type: form.reportType,
    ...(form.currencyCode.trim() === '' ? {} : { currency_code: form.currencyCode.trim() }),
  };
}

export function buildCreatePayload(form: ReportFormState): CreateReportRequest {
  return { shop_id: form.shopId as number, ...moneyFields(form) };
}

export function buildUpdatePayload(form: ReportFormState): UpdateReportRequest {
  return moneyFields(form);
}
